/**
 * Deep-link and app-store URL builders — the single place the web app spells out how a link into
 * the native app looks.
 *
 * The shape is `/open/<kind>/<id>`, exactly what the app mints in loopit-app/lib/shareLinks.ts and
 * what both routers resolve (web: router/index.tsx, app: app/open/*). Legacy `/p/:id` and internal
 * `/play/:id` are inbound-only — never mint them for links that leave the site.
 */

export type OpenKind = "game" | "drama" | "invite";
export type MobilePlatform = "ios" | "android";

/** Android applicationId (loopit-app/app.json) — the Play listing hangs off it. */
export const ANDROID_PACKAGE = "ai.muhaha.app";

/** Numeric App Store id. The fallback keeps local/dev builds on the same immutable App Store record. */
export const IOS_APP_STORE_ID =
	(import.meta.env.VITE_IOS_APP_STORE_ID ?? "").trim() || "6787895251";

/** Canonical share path, e.g. `/open/game/abc`. */
export function appOpenPath(kind: OpenKind, value: string): string {
	return `/open/${kind}/${encodeURIComponent(value)}`;
}

/** Custom-scheme link for an installed app, e.g. `muhaha://open/game/abc`. */
export function appSchemeUrl(kind: OpenKind, value: string): string {
	return `muhaha:/${appOpenPath(kind, value)}`;
}

/** Public https link, e.g. `https://muhaha.ai/open/game/abc`. */
export function webOpenUrl(
	kind: OpenKind,
	value: string,
	origin: string = window.location.origin,
): string {
	return `${origin}${appOpenPath(kind, value)}`;
}

export function androidStoreUrl(): string {
	return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
}

export function iosStoreUrl(): string {
	return `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;
}

export function storeUrl(isIOS: boolean): string {
	return isIOS ? iosStoreUrl() : androidStoreUrl();
}

export function detectMobilePlatform(
	userAgent: string,
	platform: string,
	maxTouchPoints: number,
): MobilePlatform | null {
	if (/android/i.test(userAgent)) return "android";
	if (
		/iphone|ipad|ipod/i.test(userAgent) ||
		(platform === "MacIntel" && maxTouchPoints > 1)
	) {
		return "ios";
	}
	return null;
}
